import update from 'immutability-helper';

import {
   TOGGLE_REFERENCE_PLAYING,
   TOGGLE_USER_PLAYING,
   SET_LOOKAHEAD,
   SET_INTERVAL_TIME,
   SET_PARTS_PER_BEAT,
   SET_TEMPO,
   SET_BEAT_COUNT,
   SET_INSTRUMENTS,
   ADD_INSTRUMENTS,
   TOGGLE_PART,
   ADD_NOTE_TO_PART,
   REMOVE_NOTE_FROM_PART,
   SET_VIEW,
   POP_NOTE_INTO_PENDING,
   PLACE_PENDING_NOTE,
   PATCH_PENDING_NOTE,
   SET_PENDING_NOTE,
   SET_PATTERN,
   SET_MIXER_TRACK_PAN,
   SET_MIXER_TRACK_GAIN,
   ADD_MIXER_TRACK_EFFECT,
   UPDATE_MIXER_TRACK_EFFECT,
   SET_REFERENCE_TEMPO,
   SET_TEMPOS,
   INSERT_INTO_LOOP_QUEUE,
   SET_CURRENT_LOOP_FROM_QUEUE,
   ADD_LOOP,
   LOAD_LOOP_CATEGORY_COUNT,
   ADD_LOOP_CATEGORY_COUNT,
   LOAD_LOOP,
   REPLACE_QUEUE_WITH_NEXT_LEVEL,
   SET_NEXT_LEVEL_QUEUE,
   SET_LOOP_MODE,
   TOGGLE_LOOP_MODE_PLAYING,
   TOGGLE_LOOP_MODE_REFERENCE,
   SET_SWING,
   SET_REFERENCE_SWING,
   SET_SWINGS,
   SET_CURRENT_LOOP,
   SET_SUGGESTED_VALUES,
   STOP_PLAYING,
   SET_LOOP_MODE_REFERENCE,
   SET_LOOP_MODE_PLAYING,
   SET_HAS_REFERENCE,
   SET_USER_PLAYING,
   SET_REFERENCE_PLAYING,
   SET_TRACK_PATTERN,
   SET_SEND_GAIN,
   SET_TRACK_ROUTING_ITEM,
   SET_TRACK_OUT_ROUTING,
   MOVE_MIXER_TRACK_EFFECT,
   TOGGLE_MIXER_TRACK_MUTE,
   TOGGLE_MIXER_TRACK_SOLO,
   SET_MIXER_TRACK_MUTE,
   SET_MIXER_TRACK_SOLO,
} from './actions';

import {
   ADD_SOUND_KIT,
   LOAD_SOUND_KIT,
   SET_COMPARE_ANSWER,
   SET_HIGHLIGHTED_CONTROLS,
   SET_SOUND_KIT,
} from '../../shared/actions';
import { setSoundkitReducer } from '../../shared/reducers';
import {
   sequencerInstrumentControlTypes,
   sequencerViewTypes,
} from './constants';
import { SEQUENCER_INTERFACE_TYPE } from '../interfaceTypes';

const mapInstrumentsForSequencer = (instruments, state) => {
   const now = Date.now();
   return instruments.map((instrument, i) => ({
      mixerTrack: {
         pan: { value: 0 },
         gain: { value: 1 },
         effects: [],
      },
      ...instrument,
      id: `${instrument.auxTrack ? 'aux' : instrument.source}-${i}-${now}`,
      pattern: state.loopMode
         ? []
         : instrument.pattern ||
           new Array(state.partsPerBeat * state.beatCount).fill([]),
   }));
};

const getNoteIndex = (state, action) =>
   state.instrumentRack[action.instrumentIndex].pattern[
      action.partIndex
   ].findIndex(({ note }) => note === action.note);

const sequencerReducer = (
   state = {
      type: SEQUENCER_INTERFACE_TYPE,
      hasReference: true,
      loopMode: false,
      loopModePlaying: false,
      loopModeReference: false,
      referencePlaying: false,
      userPlaying: false,
      compareAnswer: false,

      highlightedControls: {},
      suggestedValues: {},

      // Settings
      tempo: 120,
      referenceTempo: 120,
      tempoConfig: {
         range: [50, 250],
         increment: 1,
      },

      swing: 0,
      referenceSwing: 0,
      swingConfig: {},

      beatCount: 4,
      // noteRange: [-21, 3],
      noteRange: [-57, 74],

      // Advanced Settings
      lookahead: 0.2,
      intervalTime: 50,
      partsPerBeat: 24,

      view: { viewType: sequencerInstrumentControlTypes.stepSequencer },

      pianoRoll: {
         noteHeight: 30,
         beatWidth: 160,
         partSnap: 6,
         pendingNote: null,
         // stores info about the most recently placed note - otherwise used to determine length when placing a new note
         cachedNoteSettings: { noteLength: 6 },
      },

      // Instruments
      instrumentRack: [],

      // Sound Kit
      soundKit: null,
      soundKits: {},

      // Loops
      loops: {
         currentLoop: null,
         queue: [],
         nextLevelQueue: [],
         categoryCounts: {},
         cache: {},
      },

      soloTrack: null,
   },
   action
) => {
   switch (action.type) {
      case SET_HAS_REFERENCE:
         return { ...state, hasReference: action.hasReference };

      case SET_HIGHLIGHTED_CONTROLS:
         return { ...state, highlightedControls: action.highlightedControls };

      case SET_SUGGESTED_VALUES:
         return { ...state, suggestedValues: action.suggestedValues };

      case TOGGLE_REFERENCE_PLAYING:
         return update(state, {
            $toggle: ['referencePlaying'],
            userPlaying: { $set: false },
         });

      case TOGGLE_USER_PLAYING:
         return update(state, {
            $toggle: ['userPlaying'],
            referencePlaying: { $set: false },
         });

      case SET_REFERENCE_PLAYING:
         return update(state, {
            referencePlaying: { $set: action.isPlaying },
            userPlaying: { $set: false },
         });

      case SET_USER_PLAYING:
         return update(state, {
            userPlaying: { $set: action.isPlaying },
            referencePlaying: { $set: false },
         });

      case SET_LOOP_MODE:
         return {
            ...state,
            loopMode: true,
         };

      case TOGGLE_LOOP_MODE_PLAYING:
         return update(state, {
            $toggle: ['loopModePlaying'],
         });

      case SET_LOOP_MODE_PLAYING:
         return update(state, {
            loopModePlaying: { $set: action.isPlaying },
         });

      case TOGGLE_LOOP_MODE_REFERENCE:
         return update(state, {
            $toggle: ['loopModeReference'],
         });

      case SET_LOOP_MODE_REFERENCE:
         return update(state, {
            loopModeReference: { $set: action.isReference },
         });

      case STOP_PLAYING:
         return update(state, {
            userPlaying: { $set: false },
            referencePlaying: { $set: false },
            loopModePlaying: { $set: false },
            loopModeReference: { $set: state.loopMode },
         });

      case SET_COMPARE_ANSWER:
         return { ...state, compareAnswer: action.compareAnswer };

      // Settings
      case SET_TEMPO:
         return { ...state, tempo: action.tempo };

      case SET_REFERENCE_TEMPO:
         return { ...state, referenceTempo: action.tempo };

      case SET_TEMPOS:
         return {
            ...state,
            tempo: action.tempo,
            referenceTempo: action.referenceTempo,
            tempoConfig: action.tempoConfig,
         };

      case SET_SWING:
         return { ...state, swing: action.swing };

      case SET_REFERENCE_SWING:
         return { ...state, referenceSwing: action.swing };

      case SET_SWINGS:
         return {
            ...state,
            swing: action.swing,
            referenceSwing: action.referenceSwing,
            swingConfig: action.swingConfig,
         };

      case SET_BEAT_COUNT: {
         const prevPatternLength = state.beatCount * state.partsPerBeat;
         const newPatternLength = action.beatCount * state.partsPerBeat;
         return update(state, {
            beatCount: { $set: action.beatCount },
            instrumentRack: (instruments) =>
               instruments.map((instrument) => {
                  if (newPatternLength < prevPatternLength) {
                     const updateObject = {
                        pattern: { $splice: [[newPatternLength]] },
                     };
                     if (instrument.referencePattern) {
                        updateObject.referencePattern = {
                           $splice: [[newPatternLength]],
                        };
                     }

                     return update(instrument, updateObject);
                  }

                  const updateObject = {
                     pattern: {
                        $push: new Array(
                           newPatternLength - prevPatternLength
                        ).fill([]),
                     },
                  };
                  if (instrument.referencePattern) {
                     updateObject.referencePattern = {
                        $push: new Array(
                           newPatternLength - prevPatternLength
                        ).fill([]),
                     };
                  }

                  return update(instrument, updateObject);
               }),
         });
      }

      // Advanced Settings
      case SET_LOOKAHEAD:
         return { ...state, lookahead: action.lookahead };

      case SET_INTERVAL_TIME:
         return { ...state, intervalTime: action.intervalTime };

      case SET_PARTS_PER_BEAT: {
         const newPatternLength = state.beatCount * action.partsPerBeat;
         return update(state, {
            partsPerBeat: { $set: action.partsPerBeat },
            pianoRoll: {
               // could use snap divider to indicate what to divide by
               partSnap: { $set: Math.max(action.partsPerBeat / 4, 1) },
               cachedNoteSettings: {
                  noteLength: { $set: Math.max(action.partsPerBeat / 4, 1) },
               },
            },
            instrumentRack: (instruments) =>
               instruments.map((instrument) => {
                  const updateObject = {
                     pattern: {
                        $set: new Array(newPatternLength).fill([]),
                     },
                  };

                  if (instrument.referencePattern) {
                     updateObject.referencePattern = {
                        $set: new Array(newPatternLength).fill([]),
                     };
                  }
                  return update(instrument, updateObject);
               }),
         });
      }

      // View
      case SET_VIEW: {
         const updateObject = {
            view: { $set: action.viewData },
            pianoRoll: {
               cachedNoteSettings: {
                  $set: {
                     noteLength: state.pianoRoll.cachedNoteSettings.noteLength,
                  },
               },
            },
         };

         if (action.viewData.viewType === sequencerViewTypes.pianoRoll) {
            updateObject.pianoRoll.sustainAll = {
               $set: state.instrumentRack[action.viewData.instrumentIndex]
                  .sustainAll,
            };
            updateObject.pianoRoll.forcedNote = {
               $set: state.instrumentRack[action.viewData.instrumentIndex]
                  .forcedNote,
            };
            updateObject.pianoRoll.forcedStartPart = {
               $set: state.instrumentRack[action.viewData.instrumentIndex]
                  .forcedStartPart,
            };
         }

         return update(state, updateObject);
      }

      // Instruments
      case SET_INSTRUMENTS: {
         const instruments = mapInstrumentsForSequencer(
            action.instruments,
            state
         );
         return update(state, {
            instrumentRack: { $set: instruments },
         });
      }

      case ADD_INSTRUMENTS: {
         const instruments = mapInstrumentsForSequencer(
            action.instruments,
            state
         );
         return update(state, {
            instrumentRack: { $push: instruments },
         });
      }

      case TOGGLE_PART: {
         if (state.compareAnswer) {
            return state;
         }

         const partHasValue =
            state.instrumentRack[action.instrumentIndex].pattern[
               action.partIndex
            ].length > 0;
         if (partHasValue) {
            return update(state, {
               instrumentRack: {
                  [action.instrumentIndex]: {
                     pattern: { [action.partIndex]: { $set: [] } },
                  },
               },
            });
         }
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  pattern: {
                     [action.partIndex]: {
                        $set: [{ note: 0, startPart: action.partIndex }],
                     },
                  },
               },
            },
         });
      }

      case ADD_NOTE_TO_PART: {
         const noteIndex = getNoteIndex(state, action);
         if (noteIndex > -1) {
            return state;
         }
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  pattern: {
                     [action.partIndex]: {
                        $push: [
                           {
                              ...state.pianoRoll.cachedNoteSettings,
                              note: action.note,
                              startPart: action.partIndex,
                           },
                        ],
                     },
                  },
               },
            },
         });
      }

      case REMOVE_NOTE_FROM_PART: {
         const noteIndex = getNoteIndex(state, action);
         if (noteIndex === -1) {
            return state;
         }
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  pattern: {
                     [action.partIndex]: { $splice: [[noteIndex, 1]] },
                  },
               },
            },
         });
      }

      // Sound Kit
      case LOAD_SOUND_KIT: {
         let { soundKitId } = action;
         if (soundKitId == null) {
            soundKitId = action.soundKit.id;
         }

         return update(state, {
            soundKits: { [soundKitId]: { $set: { loading: true } } },
         });
      }

      case ADD_SOUND_KIT:
         return update(state, {
            soundKits: { [action.soundKit.id]: { $set: action.soundKit } },
         });

      case SET_SOUND_KIT:
         return setSoundkitReducer(state, action);

      // Piano Roll
      case POP_NOTE_INTO_PENDING: {
         const noteIndex = getNoteIndex(state, action);
         const note =
            state.instrumentRack[action.instrumentIndex].pattern[
               action.partIndex
            ][noteIndex];
         return update(state, {
            pianoRoll: { pendingNote: { $set: note } },
            instrumentRack: {
               [action.instrumentIndex]: {
                  pattern: {
                     [action.partIndex]: { $splice: [[noteIndex, 1]] },
                  },
               },
            },
         });
      }

      case SET_PENDING_NOTE: {
         return update(state, {
            pianoRoll: {
               pendingNote: {
                  $set: action.pendingNote,
               },
            },
         });
      }

      case PLACE_PENDING_NOTE: {
         const {
            startPart,
            note: noteTone,
            noteLength,
         } = state.pianoRoll.pendingNote;

         let lengthOverride = noteLength;

         if (startPart + noteLength > state.beatCount * state.partsPerBeat) {
            lengthOverride = state.beatCount * state.partsPerBeat - startPart;
         }

         const newPattern = state.instrumentRack[
            state.view.instrumentIndex
         ].pattern.map((notes, i) => {
            const newNotes = notes.reduce((mappedNotes, currentNote) => {
               // If note is not at same tone value, just add the note and return the array
               if (noteTone !== currentNote.note) {
                  return [...mappedNotes, currentNote];
               }
               // If note starts during new note, remove note
               else if (
                  (currentNote.startPart >= startPart &&
                     currentNote.startPart < startPart + lengthOverride) ||
                  (currentNote.startPart === startPart && noteLength == null)
               ) {
                  return mappedNotes;
               }
               // If note starts before new note and would overlap new note, trim old note
               else if (
                  currentNote.startPart < startPart &&
                  currentNote.startPart + currentNote.noteLength > startPart
               ) {
                  return [
                     ...mappedNotes,
                     {
                        ...currentNote,
                        noteLength: startPart - currentNote.startPart,
                     },
                  ];
               }

               return [...mappedNotes, currentNote];
            }, []);

            // Add new note to appropriate location
            if (i === startPart) {
               return [
                  ...newNotes,
                  {
                     ...state.pianoRoll.pendingNote,
                     noteLength: lengthOverride,
                  },
               ];
            }
            return newNotes;
         });

         const updateObject = {
            pianoRoll: {
               pendingNote: { $set: null },
               cachedNoteSettings: {
                  $set: state.pianoRoll.pendingNote,
               },
            },
            instrumentRack: {
               [state.view.instrumentIndex]: {
                  pattern: {
                     $set: newPattern,
                  },
               },
            },
         };

         // DON'T cache sustain all undef note lengths
         if (state.pianoRoll.sustainAll) {
            updateObject.pianoRoll.cachedNoteSettings.$set = {
               ...state.pianoRoll.pendingNote,
               noteLength: state.pianoRoll.cachedNoteSettings.noteLength,
            };
         }

         return update(state, updateObject);
      }

      case PATCH_PENDING_NOTE: {
         return update(state, {
            pianoRoll: {
               pendingNote: {
                  $set: { ...state.pianoRoll.pendingNote, ...action.note },
               },
            },
         });
      }

      case SET_PATTERN: {
         return update(state, {
            instrumentRack: action.pattern.reduce(
               (acc, value, index) => ({
                  ...acc,
                  [index]: {
                     [action.isReference ? 'referencePattern' : 'pattern']: {
                        $set: value,
                     },
                  },
               }),
               {}
            ),
         });
      }

      case SET_TRACK_PATTERN: {
         return update(state, {
            instrumentRack: {
               [action.trackIndex]: {
                  [action.isReference ? 'referencePattern' : 'pattern']: {
                     $set: action.pattern,
                  },
               },
            },
         });
      }

      // Mixer
      case SET_MIXER_TRACK_PAN: {
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  mixerTrack: {
                     pan: { value: { $set: action.pan } },
                  },
               },
            },
         });
      }

      case SET_MIXER_TRACK_GAIN: {
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  mixerTrack: {
                     gain: { value: { $set: action.gain } },
                  },
               },
            },
         });
      }

      case ADD_MIXER_TRACK_EFFECT: {
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  mixerTrack: {
                     effects: { $push: [action.effect] },
                  },
               },
            },
         });
      }

      case UPDATE_MIXER_TRACK_EFFECT: {
         return update(state, {
            instrumentRack: {
               [action.instrumentIndex]: {
                  [action.reference ? 'referenceMixerTrack' : 'mixerTrack']: {
                     effects: {
                        [action.effectIndex]: { settings: action.update },
                     },
                  },
               },
            },
         });
      }

      case MOVE_MIXER_TRACK_EFFECT: {
         const { instrumentIndex } = state.view;
         const effectList =
            state.instrumentRack[instrumentIndex].mixerTrack.effects;

         const activeEffect = effectList[action.activeIndex];
         let tempList = update(effectList, {
            $splice: [[action.activeIndex, 1]],
         });
         tempList = update(tempList, {
            $splice: [[action.overIndex, 0, activeEffect]],
         });

         return update(state, {
            instrumentRack: {
               [instrumentIndex]: {
                  mixerTrack: {
                     effects: {
                        $set: tempList,
                     },
                  },
               },
            },
         });
      }

      case INSERT_INTO_LOOP_QUEUE: {
         const queuePropertyName = action.useNextLevelQueue
            ? 'nextLevelQueue'
            : 'queue';

         return update(state, {
            loops: {
               [queuePropertyName]: { $push: action.insertArray },
            },
         });
      }

      case SET_CURRENT_LOOP_FROM_QUEUE: {
         const queuePropertyName = action.useNextLevelQueue
            ? 'nextLevelQueue'
            : 'queue';
         const { loopKitId, categoryId, loopId } =
            state.loops[queuePropertyName][0];
         const cachedLoopPropertyName = `${loopKitId}/${categoryId}/${loopId}`;

         const cachedLoop = state.loops.cache[cachedLoopPropertyName];
         const loopIsLoaded = cachedLoop && !cachedLoop.loading;

         const loopsUpdateConfig = {
            currentLoop: { $set: cachedLoopPropertyName },
            [queuePropertyName]: { $splice: [[0, 1]] },
         };

         if (loopIsLoaded) {
            loopsUpdateConfig.cache = {
               [cachedLoopPropertyName]: { lastAccessed: { $set: Date.now() } },
            };
         }

         return update(state, {
            loops: loopsUpdateConfig,
         });
      }

      case SET_CURRENT_LOOP: {
         return update(state, {
            loops: {
               currentLoop: { $set: action.loop },
            },
         });
      }

      case LOAD_LOOP: {
         return update(state, {
            loops: {
               cache: {
                  [`${action.loopKitId}/${action.categoryId}/${action.loopId}`]:
                     { $set: { loading: true } },
               },
            },
         });
      }

      case ADD_LOOP: {
         const updateCacheConfig = {
            [action.cacheId]: { $set: action.loop },
         };

         const { cache } = state.loops;
         const cacheEntries = Object.entries(cache);
         if (cacheEntries.length === 10) {
            updateCacheConfig.$unset = [
               cacheEntries.reduce((oldest, current) => {
                  if (oldest[1].lastAccessed > current[1].lastAccessed) {
                     return current;
                  }
                  return oldest;
               })[0],
            ];
         }

         return update(state, {
            loops: {
               cache: updateCacheConfig,
            },
         });
      }

      case LOAD_LOOP_CATEGORY_COUNT: {
         return update(state, {
            loops: {
               categoryCounts: {
                  [`${action.loopKitId}/${action.categoryId}`]: {
                     $set: { loading: true },
                  },
               },
            },
         });
      }

      case ADD_LOOP_CATEGORY_COUNT: {
         return update(state, {
            loops: {
               categoryCounts: {
                  [`${action.loopKitId}/${action.categoryId}`]: {
                     $set: action.count,
                  },
               },
            },
         });
      }

      case REPLACE_QUEUE_WITH_NEXT_LEVEL: {
         return update(state, {
            loops: {
               queue: { $set: state.loops.nextLevelQueue },
               nextLevelQueue: { $set: [] },
            },
         });
      }

      case SET_NEXT_LEVEL_QUEUE: {
         return update(state, {
            loops: {
               nextLevelQueue: { $set: action.queue },
            },
         });
      }

      case SET_TRACK_OUT_ROUTING: {
         return update(state, {
            instrumentRack: {
               [action.trackIndex]: {
                  [action.reference ? 'referenceMixerTrack' : 'mixerTrack']: {
                     sendOut: {
                        $set: action.isRouted,
                     },
                  },
               },
            },
         });
      }

      case SET_TRACK_ROUTING_ITEM: {
         const toTrackId = state.instrumentRack[action.toTrackIndex].id;

         if (action.isRouted) {
            return update(state, {
               instrumentRack: {
                  [action.fromTrackIndex]: {
                     [action.reference ? 'referenceMixerTrack' : 'mixerTrack']:
                        {
                           sendTo: {
                              $set: {
                                 ...state.instrumentRack[action.fromTrackIndex][
                                    action.reference
                                       ? 'referenceMixerTrack'
                                       : 'mixerTrack'
                                 ].sendTo,
                                 [toTrackId]: 1,
                              },
                           },
                        },
                  },
               },
            });
         } else {
            return update(state, {
               instrumentRack: {
                  [action.fromTrackIndex]: {
                     [action.reference ? 'referenceMixerTrack' : 'mixerTrack']:
                        {
                           sendTo: {
                              $set: {
                                 ...state.instrumentRack[action.fromTrackIndex][
                                    action.reference
                                       ? 'referenceMixerTrack'
                                       : 'mixerTrack'
                                 ].sendTo,
                                 [toTrackId]: undefined,
                              },
                           },
                        },
                  },
               },
            });
         }
      }

      case SET_SEND_GAIN: {
         const toTrackId = state.instrumentRack[action.toTrackIndex].id;

         return update(state, {
            instrumentRack: {
               [action.fromTrackIndex]: {
                  [action.reference ? 'referenceMixerTrack' : 'mixerTrack']: {
                     sendTo: {
                        [toTrackId]: { $set: action.gain },
                     },
                  },
               },
            },
         });
      }

      case TOGGLE_MIXER_TRACK_MUTE:
      case SET_MIXER_TRACK_MUTE: {
         const trackUpdate =
            action.type === SET_MIXER_TRACK_MUTE
               ? {
                    muted: { $set: action.mute },
                 }
               : {
                    $toggle: ['muted'],
                 };

         const updateObject = {
            instrumentRack: {
               [action.trackIndex]: {
                  [action.reference ? 'referenceMixerTrack' : 'mixerTrack']:
                     trackUpdate,
               },
            },
         };

         if (state.soloTrack === action.trackIndex) {
            updateObject.soloTrack = { $set: null };
         }

         return update(state, updateObject);
      }

      case TOGGLE_MIXER_TRACK_SOLO:
      case SET_MIXER_TRACK_SOLO: {
         const toggleOff =
            (action.type === SET_MIXER_TRACK_SOLO && !action.solo) ||
            (action.type === TOGGLE_MIXER_TRACK_SOLO &&
               state.soloTrack === action.trackIndex);

         return update(state, {
            soloTrack: { $set: toggleOff ? null : action.trackIndex },
            instrumentRack: {
               [action.trackIndex]: {
                  [action.reference ? 'referenceMixerTrack' : 'mixerTrack']: {
                     muted: { $set: false },
                  },
               },
            },
         });
      }

      default:
         return state;
   }
};

export { sequencerReducer };
